import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Zoom,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";

// Register Chart.js components and plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  zoomPlugin
);

export default function BarChart({ data, title }) {
  // Determine if the data is for a single graph or multiple graphs
  const isMultipleGraphs = typeof Object.values(data)[0] === "object";

  // Prepare data for Chart.js
  const chartData = isMultipleGraphs
    ? {
        labels: Object.keys(data), // e.g., ["l1", "l2", "l3", "l4"]
        datasets: Object.keys(Object.values(data)[0]).map((metric, index) => ({
          label: metric, // e.g., "precision", "recall", "f1_score", "ap"
          data: Object.values(data).map((item) => item[metric]), // Extract values for each metric
          backgroundColor: [
            "rgb(136, 132, 216)",
            "rgb(130, 202, 157)",
            "rgb(255, 198, 88)",
            "rgb(255, 128, 66)",
          ][index % 4],
          borderColor: [
            "rgb(136, 132, 216)",
            "rgb(130, 202, 157)",
            "rgb(255, 198, 88)",
            "rgb(255, 128, 66)",
          ][index % 4],
          borderWidth: 0, // Removed border lines
        })),
      }
    : {
        labels: Object.keys(data), // e.g., ["precision", "recall", "f1_score", "mAP"]
        datasets: [
          {
            label: "Evaluation Metrics", // e.g., "precision", "recall", "f1_score", "mAP"
            data: Object.values(data), // e.g., [0.28, 0.337, 0.3, 0.28]
            backgroundColor: [
              "rgb(136, 132, 216)",
              "rgb(130, 202, 157)",
              "rgb(255, 198, 88)",
              "rgb(255, 128, 66)",
            ],
            borderColor: [
              "rgb(136, 132, 216)",
              "rgb(130, 202, 157)",
              "rgb(255, 198, 88)",
              "rgb(255, 128, 66)",
            ],
            borderWidth: 0, // Removed border lines
          },
        ],
      };

  // Chart.js options
  const options = {
    responsive: true,
    maintainAspectRatio: false, // Allow the chart to fit within the parent container
    plugins: {
      legend: {
        display: isMultipleGraphs, // Hide legend for single bar chart
        position: "top",
        labels: {
          font: {
            size: 14,
          },
        },
      },      
      tooltip: {
        enabled: false, // Disable the default tooltip
        external: function (context) {
          // Custom tooltip rendering to support HTML
          const tooltipEl = document.getElementById('chartjs-tooltip');

          if (!tooltipEl) {
            const div = document.createElement('div');
            div.id = 'chartjs-tooltip';
            div.style.position = 'absolute';
            div.style.background = 'rgba(255, 255, 255, 0.8)';
            div.style.border = '1px solid #ccc';
            div.style.borderRadius = '5px';
            div.style.padding = '10px';
            div.style.pointerEvents = 'none';
            div.style.zIndex = '100';
            document.body.appendChild(div);
          }

          const tooltipModel = context.tooltip;
          const tooltipElUpdated = document.getElementById('chartjs-tooltip');

          if (tooltipModel.opacity === 0) {
            tooltipElUpdated.style.opacity = 0;
            return;
          }

          const dataIndex = tooltipModel.dataPoints[0].dataIndex;
          if (isMultipleGraphs) {
            const bodyHtml = context.chart.data.datasets.map((dataset) => {
              const value = dataset.data[dataIndex];
              const color = dataset.backgroundColor;
              return `<div style='display: flex; align-items: center; margin-bottom: 5px;'>
                        <span style='width: 12px; height: 12px; background-color: ${color}; display: inline-block; margin-right: 8px;'></span>
                        <span>${dataset.label}: ${value}</span>
                      </div>`;
            }).join('');

            tooltipElUpdated.innerHTML = bodyHtml;
          } else {
            const dataset = context.chart.data.datasets[0];
            const metric = context.chart.data.labels[dataIndex];
            const value = dataset.data[dataIndex];
            const color = dataset.backgroundColor[dataIndex];

            const bodyHtml = `<div style='display: flex; align-items: center; margin-bottom: 5px;'>
                                <span style='width: 12px; height: 12px; background-color: ${color}; display: inline-block; margin-right: 8px;'></span>
                                <span>${metric}: ${value}</span>
                              </div>`;

            tooltipElUpdated.innerHTML = bodyHtml;
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipElUpdated.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipElUpdated.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
          tooltipElUpdated.style.opacity = 1;
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 18,
        },
      },
      datalabels: {
        anchor: "end",
        align: "top",
        formatter: (value) => value.toFixed(2), // Show values on top of bars
        font: {
          size: 14, // Larger font size
          weight: "bold", // Bold font
        },
        color: "#4A4A4A", // Darker color for better visibility
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x", // Allow panning in the x-axis
        },
        zoom: {
          wheel: {
            enabled: true, // Enable zooming with the mouse wheel
          },
          pinch: {
            enabled: true, // Enable zooming with pinch gestures
          },
          mode: "x", // Allow zooming in the x-axis
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 1, // Y-axis fixed between 0 and 1
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
  };

  // Adjust the chart to fit the height of the parent card
  return (
    <div className="w-full h-full p-4 bg-white shadow-md rounded-md" style={{ height: '100%', overflow: 'hidden' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
}